import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import type { BugReportDocument, BugReportType, BugReportStatus } from '@schemas/firestore';

const REPORT_TYPES: { value: BugReportType | 'all'; label: string; icon: string }[] = [
    { value: 'all', label: 'All Types', icon: '📋' },
    { value: 'bug', label: 'Bugs', icon: '🐛' },
    { value: 'feature', label: 'Features', icon: '✨' },
    { value: 'improvement', label: 'Improvements', icon: '💡' },
];

const STATUS_OPTIONS: { value: BugReportStatus; label: string; color: string }[] = [
    { value: 'open', label: 'Open', color: 'bg-blue-500' },
    { value: 'acknowledged', label: 'Acknowledged', color: 'bg-yellow-500' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-purple-500' },
    { value: 'resolved', label: 'Resolved', color: 'bg-green-500' },
    { value: 'wont_fix', label: "Won't Fix", color: 'bg-gray-500' },
];

const STATUS_FILTERS: { value: BugReportStatus | 'all' | 'active'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'open', label: 'Open' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'wont_fix', label: "Won't Fix" },
];

interface BugReport extends Omit<BugReportDocument, 'createdAt' | 'updatedAt' | 'resolvedAt'> {
    createdAt: Date;
    updatedAt: Date;
    resolvedAt?: Date;
}

export default function AdminBugReportsPage() {
    const { user, userDoc } = useAuth();
    const [reports, setReports] = useState<BugReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedReport, setSelectedReport] = useState<BugReport | null>(null);
    const [statusFilter, setStatusFilter] = useState<BugReportStatus | 'all' | 'active'>('active');
    const [typeFilter, setTypeFilter] = useState<BugReportType | 'all'>('all');
    const [adminNotes, setAdminNotes] = useState('');
    const [updating, setUpdating] = useState(false);

    const isAdmin = userDoc?.role === 'admin' || userDoc?.role === 'coach';

    // Load all bug reports
    useEffect(() => {
        async function loadReports() {
            if (!isAdmin) return;

            try {
                const reportsQuery = query(
                    collection(db, 'bugReports'),
                    orderBy('createdAt', 'desc')
                );
                const snapshot = await getDocs(reportsQuery);
                const loadedReports = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        title: data.title,
                        description: data.description,
                        type: data.type as BugReportType,
                        screenshotUrl: data.screenshotUrl,
                        screenshotPath: data.screenshotPath,
                        reportedBy: data.reportedBy,
                        reporterEmail: data.reporterEmail,
                        reporterName: data.reporterName,
                        status: data.status as BugReportStatus,
                        adminNotes: data.adminNotes,
                        resolvedBy: data.resolvedBy,
                        affectedPage: data.affectedPage,
                        createdAt: data.createdAt?.toDate() || new Date(),
                        updatedAt: data.updatedAt?.toDate() || new Date(),
                        resolvedAt: data.resolvedAt?.toDate(),
                    } as BugReport;
                });
                setReports(loadedReports);
            } catch (err) {
                console.error('Error loading reports:', err);
            } finally {
                setLoading(false);
            }
        }

        loadReports();
    }, [isAdmin]);

    // Filter reports
    const filteredReports = reports.filter(report => {
        // Status filter
        if (statusFilter === 'active') {
            if (['resolved', 'wont_fix'].includes(report.status)) return false;
        } else if (statusFilter !== 'all') {
            if (report.status !== statusFilter) return false;
        }

        // Type filter
        if (typeFilter !== 'all' && report.type !== typeFilter) return false;

        return true;
    });

    // Open report details
    const openReport = (report: BugReport) => {
        setSelectedReport(report);
        setAdminNotes(report.adminNotes || '');
    };

    // Update report status
    const updateStatus = async (newStatus: BugReportStatus) => {
        if (!selectedReport || !user) return;

        setUpdating(true);
        try {
            const updates: Record<string, unknown> = {
                status: newStatus,
                adminNotes: adminNotes.trim() || null,
                updatedAt: Timestamp.now(),
            };

            if (newStatus === 'resolved' || newStatus === 'wont_fix') {
                updates.resolvedBy = user.uid;
                updates.resolvedAt = Timestamp.now();
            }

            await updateDoc(doc(db, 'bugReports', selectedReport.id), updates);

            // Update local state
            setReports(prev =>
                prev.map(r =>
                    r.id === selectedReport.id
                        ? {
                            ...r,
                            status: newStatus,
                            adminNotes: adminNotes.trim() || undefined,
                            updatedAt: new Date(),
                            resolvedBy: ['resolved', 'wont_fix'].includes(newStatus) ? user.uid : r.resolvedBy,
                            resolvedAt: ['resolved', 'wont_fix'].includes(newStatus) ? new Date() : r.resolvedAt,
                        }
                        : r
                )
            );

            setSelectedReport(prev =>
                prev
                    ? {
                        ...prev,
                        status: newStatus,
                        adminNotes: adminNotes.trim() || undefined,
                        updatedAt: new Date(),
                    }
                    : null
            );
        } catch (err) {
            console.error('Error updating report:', err);
        } finally {
            setUpdating(false);
        }
    };

    // Save admin notes without changing status
    const saveNotes = async () => {
        if (!selectedReport) return;

        setUpdating(true);
        try {
            await updateDoc(doc(db, 'bugReports', selectedReport.id), {
                adminNotes: adminNotes.trim() || null,
                updatedAt: Timestamp.now(),
            });

            setReports(prev =>
                prev.map(r =>
                    r.id === selectedReport.id
                        ? { ...r, adminNotes: adminNotes.trim() || undefined, updatedAt: new Date() }
                        : r
                )
            );

            setSelectedReport(prev =>
                prev ? { ...prev, adminNotes: adminNotes.trim() || undefined, updatedAt: new Date() } : null
            );
        } catch (err) {
            console.error('Error saving notes:', err);
        } finally {
            setUpdating(false);
        }
    };

    if (!isAdmin) {
        return (
            <div className="p-6 lg:p-8 max-w-5xl">
                <div className="text-center py-12">
                    <span className="text-4xl block mb-4">🔒</span>
                    <p className="text-secondary-400">You don't have permission to view this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-6xl">
            {/* Header */}
            <div className="mb-8 animate-fade-in">
                <h1 className="text-3xl font-display font-bold text-white">
                    Bug Reports & Feature Requests
                </h1>
                <p className="text-secondary-400 mt-1">
                    Review and manage feedback from players and staff
                </p>
            </div>

            <div className="lg:flex lg:gap-6">
                {/* Report List */}
                <div className="lg:w-1/2 mb-6 lg:mb-0">
                    {/* Filters */}
                    <div className="card mb-4 animate-slide-up">
                        <div className="flex flex-wrap gap-4">
                            {/* Status Filter */}
                            <div className="flex-1 min-w-[150px]">
                                <label className="block text-xs font-medium text-secondary-400 mb-1">
                                    Status
                                </label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                                    className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                                >
                                    {STATUS_FILTERS.map(({ value, label }) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Type Filter */}
                            <div className="flex-1 min-w-[150px]">
                                <label className="block text-xs font-medium text-secondary-400 mb-1">
                                    Type
                                </label>
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
                                    className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                                >
                                    {REPORT_TYPES.map(({ value, label }) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Reports List */}
                    <div className="card animate-slide-up" style={{ animationDelay: '0.1s' }}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-white">
                                Reports ({filteredReports.length})
                            </h2>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : filteredReports.length === 0 ? (
                            <p className="text-secondary-400 text-center py-8">
                                No reports match your filters.
                            </p>
                        ) : (
                            <div className="space-y-2 max-h-[600px] overflow-y-auto">
                                {filteredReports.map(report => (
                                    <button
                                        key={report.id}
                                        onClick={() => openReport(report)}
                                        className={`w-full text-left p-4 rounded-lg border transition-all ${selectedReport?.id === report.id
                                                ? 'border-primary-500 bg-primary-500/10'
                                                : 'border-secondary-700 bg-secondary-800/50 hover:border-secondary-600'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <span className="text-lg flex-shrink-0">
                                                    {REPORT_TYPES.find(t => t.value === report.type)?.icon || '📋'}
                                                </span>
                                                <div className="min-w-0">
                                                    <p className="text-white font-medium truncate">
                                                        {report.title}
                                                    </p>
                                                    <p className="text-secondary-500 text-sm">
                                                        {report.reporterName} • {report.createdAt.toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <span
                                                className={`px-2 py-1 rounded text-xs font-medium text-white flex-shrink-0 ${STATUS_OPTIONS.find(s => s.value === report.status)?.color || 'bg-gray-500'
                                                    }`}
                                            >
                                                {STATUS_OPTIONS.find(s => s.value === report.status)?.label || report.status}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Report Details */}
                <div className="lg:w-1/2">
                    <div className="card animate-slide-up sticky top-6" style={{ animationDelay: '0.2s' }}>
                        {selectedReport ? (
                            <>
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl">
                                            {REPORT_TYPES.find(t => t.value === selectedReport.type)?.icon || '📋'}
                                        </span>
                                        <div>
                                            <h2 className="text-xl font-semibold text-white">
                                                {selectedReport.title}
                                            </h2>
                                            <p className="text-secondary-400 text-sm">
                                                {REPORT_TYPES.find(t => t.value === selectedReport.type)?.label}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setSelectedReport(null)}
                                        className="text-secondary-400 hover:text-white transition-colors"
                                        aria-label="Close details"
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* Reporter Info */}
                                <div className="mb-4 p-3 bg-secondary-800/50 rounded-lg">
                                    <div className="flex items-center justify-between text-sm">
                                        <div>
                                            <span className="text-secondary-400">Reported by: </span>
                                            <span className="text-white">{selectedReport.reporterName}</span>
                                        </div>
                                        <span className="text-secondary-500">
                                            {selectedReport.createdAt.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="text-sm text-secondary-400 mt-1">
                                        {selectedReport.reporterEmail}
                                    </div>
                                    {selectedReport.affectedPage && (
                                        <div className="text-sm text-secondary-500 mt-1">
                                            Page: {selectedReport.affectedPage}
                                        </div>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="mb-4">
                                    <h3 className="text-sm font-medium text-secondary-300 mb-2">Description</h3>
                                    <p className="text-white whitespace-pre-wrap bg-secondary-800/50 p-4 rounded-lg">
                                        {selectedReport.description}
                                    </p>
                                </div>

                                {/* Screenshot */}
                                {selectedReport.screenshotUrl && (
                                    <div className="mb-4">
                                        <h3 className="text-sm font-medium text-secondary-300 mb-2">Screenshot</h3>
                                        <a
                                            href={selectedReport.screenshotUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block"
                                        >
                                            <img
                                                src={selectedReport.screenshotUrl}
                                                alt="Bug screenshot"
                                                className="max-w-full max-h-64 rounded-lg border border-secondary-600 hover:border-primary-500 transition-colors"
                                            />
                                        </a>
                                    </div>
                                )}

                                {/* Status Update */}
                                <div className="mb-4">
                                    <h3 className="text-sm font-medium text-secondary-300 mb-2">Status</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {STATUS_OPTIONS.map(({ value, label, color }) => (
                                            <button
                                                key={value}
                                                onClick={() => updateStatus(value)}
                                                disabled={updating || selectedReport.status === value}
                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedReport.status === value
                                                        ? `${color} text-white`
                                                        : 'bg-secondary-700 text-secondary-300 hover:bg-secondary-600'
                                                    } disabled:opacity-50`}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Admin Notes */}
                                <div>
                                    <h3 className="text-sm font-medium text-secondary-300 mb-2">Admin Notes</h3>
                                    <textarea
                                        value={adminNotes}
                                        onChange={(e) => setAdminNotes(e.target.value)}
                                        placeholder="Internal notes about this report..."
                                        rows={3}
                                        className="w-full px-4 py-3 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500 transition-colors resize-none mb-2"
                                    />
                                    <button
                                        onClick={saveNotes}
                                        disabled={updating || adminNotes === (selectedReport.adminNotes || '')}
                                        className="btn-secondary text-sm disabled:opacity-50"
                                    >
                                        {updating ? 'Saving...' : 'Save Notes'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <span className="text-4xl block mb-4">📋</span>
                                <p className="text-secondary-400">
                                    Select a report to view details
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
